import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLazyCreateOrderQuery } from '../redux/api/authorized/ultra';
import { useGetGiftFeesQuery } from '../redux/api/authorized/giftFees';
import {
  selectTokenByNetworkAndSymbol,
  selectTokenByNetworkAndAddress,
  selectTokensWithUSDBalance,
} from '../redux/slices/tokenSlice';
import { calculateApplePayFeeAndTotal } from './useApplePay';
import { calculateTokenQuote } from './useTokenQuote';

/**
 * Helper function to filter tokens by balance
 * Returns tokens with enough balance and tokens without enough balance
 *
 * @param {Array} tokensWithUSDBalance - Array of tokens with USD balance information
 * @param {number|string} requiredAmount - The amount required (in USD)
 * @returns {{ tokensWithEnoughBalance: Array, tokensWithoutEnoughBalance: Array }}
 */
export const filterTokensByBalance = (tokensWithUSDBalance, requiredAmount) => {
  if (!tokensWithUSDBalance || !requiredAmount) {
    return {
      tokensWithEnoughBalance: [],
      tokensWithoutEnoughBalance: [],
    };
  }

  const amountNum = parseFloat(requiredAmount || '0');
  const tokensWithEnoughBalance = tokensWithUSDBalance.filter(
    (token) => token.usdValue >= amountNum,
  );
  const tokensWithoutEnoughBalance = tokensWithUSDBalance.filter(
    (token) => token.usdValue < amountNum,
  );

  return {
    tokensWithEnoughBalance,
    tokensWithoutEnoughBalance,
  };
};

/**
 * Hook that handles all gift amount calculations:
 * - Token amount conversions (USD ↔ token)
 * - Fee calculations (shipping, Apple Pay, total)
 * - Balance filtering (tokens with enough balance)
 *
 * This extracts the complex calculation logic from the Confirmation component
 * to make it clearer and more maintainable.
 */
export const useGiftAmountCalculations = ({
  // Form values
  network,
  targetTokenAddress,
  targetTokenAmount,
  usdAmount,
  fromUsdcDirection,
  delivery,

  // Payment selection
  selectedPaymentMethodType,
}) => {
  // Get tokens from Redux
  const usdcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, network || 'solana', 'USDC'),
  );

  const targetToken = useSelector((state) =>
    targetTokenAddress && network
      ? selectTokenByNetworkAndAddress(state, network, targetTokenAddress)
      : null,
  );

  const tokensWithUSDBalance = useSelector(selectTokensWithUSDBalance);

  // API hooks
  const [triggerCreateOrder] = useLazyCreateOrderQuery();

  const { data: giftFeesResponse, isLoading: isLoadingGiftFees } =
    useGetGiftFeesQuery(
      {
        token: targetToken?.address,
        tokenAmount: targetTokenAmount,
        delivery: delivery,
      },
      {
        skip: !targetToken?.address || !targetTokenAmount || !delivery,
        refetchOnMountOrArgChange: true,
        keepUnusedDataFor: 0,
      },
    );

  // State for calculated amounts
  const [formatedTokenAmount, setFormatedTokenAmount] = useState(0);
  const [formatedAmountUSD, setFormatedAmountUSD] = useState(0);
  const [tokensWithEnoughBalance, setTokensWithEnoughBalance] = useState(null);

  /**
   * Calculate token amounts and USD conversions
   * This handles the conversion between USD and target token amounts
   * based on the direction the user entered the amount.
   */
  useEffect(() => {
    (async () => {
      // Basic null checks
      if (!usdcToken?.address || !targetToken?.address) {
        return;
      }

      // If target token is USDC, set values directly (no conversion needed)
      if (usdcToken.address === targetToken.address) {
        if (usdAmount) {
          setFormatedTokenAmount(targetTokenAmount);
          setFormatedAmountUSD(usdAmount);
        }
        return;
      }

      // For non-USDC target tokens, calculate based on direction
      if (fromUsdcDirection) {
        // User entered USD amount - set it immediately (no need to wait for API call)
        if (usdAmount) {
          setFormatedAmountUSD(usdAmount);

          // Convert to target token (async, but don't block USD display)
          if (triggerCreateOrder) {
            const formattedTokenAmount = await calculateTokenQuote({
              triggerCreateOrder,
              fromToken: usdcToken,
              toToken: targetToken,
              amount: usdAmount,
            });

            if (formattedTokenAmount) {
              setFormatedTokenAmount(formattedTokenAmount);
            }
          }
        }
      } else {
        // User entered target token amount, convert to USD
        if (targetTokenAmount && triggerCreateOrder) {
          const formattedUsdAmount = await calculateTokenQuote({
            triggerCreateOrder,
            fromToken: targetToken,
            toToken: usdcToken,
            amount: targetTokenAmount,
          });

          if (formattedUsdAmount) {
            setFormatedTokenAmount(targetTokenAmount);
            setFormatedAmountUSD(formattedUsdAmount);
          }
        }
      }
    })();
  }, [
    fromUsdcDirection,
    targetTokenAmount,
    usdAmount,
    targetToken,
    usdcToken,
    triggerCreateOrder,
  ]);

  /**
   * Calculate all fees (gift amount, shipping, Apple Pay fee, total)
   * This is the central place for all fee calculations.
   */
  const feeCalculations = useMemo(() => {
    const giftAmount = parseFloat(formatedAmountUSD || 0);
    const shippingFees = parseFloat(giftFeesResponse?.totalFees?.usdFees || 0);
    const usdDiscount = parseFloat(
      giftFeesResponse?.totalFees?.usdDiscount || 0,
    );
    const baseAmount = giftAmount + shippingFees; // Amount before Apple Pay fee

    // Calculate Apple Pay fee and total using the correct inverse formula
    // Fee is taken from the total amount charged, so we use the shared calculation
    const { fee: applePayFee, total: totalAmount } =
      selectedPaymentMethodType === 'applePay'
        ? calculateApplePayFeeAndTotal(baseAmount)
        : { fee: 0, total: baseAmount };

    return {
      giftAmount,
      shippingFees,
      usdDiscount,
      applePayFee,
      totalAmount,
      applePayBaseAmount: baseAmount, // Amount sent to Apple Pay (they add fee on top)
    };
  }, [
    formatedAmountUSD,
    giftFeesResponse, // Use whole object instead of nested property to ensure updates are detected
    selectedPaymentMethodType,
    calculateApplePayFeeAndTotal, // Include function reference for dependency tracking
  ]);

  /**
   * Filter tokens by balance - determine which tokens have enough balance
   * to cover the total amount (gift + shipping fees)
   */
  useEffect(() => {
    const totalAmountWithFees =
      feeCalculations.giftAmount + feeCalculations.shippingFees;

    if (tokensWithUSDBalance && totalAmountWithFees) {
      const { tokensWithEnoughBalance: filteredTokens } = filterTokensByBalance(
        tokensWithUSDBalance,
        totalAmountWithFees,
      );
      setTokensWithEnoughBalance(filteredTokens);
    } else {
      // Reset to null if we don't have the data yet
      setTokensWithEnoughBalance(null);
    }
  }, [tokensWithUSDBalance, feeCalculations]);

  return {
    // Calculated amounts
    formatedTokenAmount,
    formatedAmountUSD,

    // Fee calculations
    feeCalculations,
    giftFeesResponse, // Raw fee response for components that need it

    // Token data
    usdcToken,
    targetToken,

    // Balance filtering
    tokensWithEnoughBalance,
    tokensWithUSDBalance,

    // Loading state
    isLoadingGiftFees,

    // API function for use in component (e.g., for gift creation)
    triggerCreateOrder,
  };
};
